import { ComponentType, FunctionComponent } from "react";

import { InvertOptional } from "./types";

/**
 * Get the display name of a react component.
 */
export const getDisplayName = <
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    T extends ComponentType<any>,
>(
    WrappedComponent: T,
): string => {
    return WrappedComponent.displayName || WrappedComponent.name || "Component";
};

/**
 * Component decorator for supplying default props. Useful since
 * Component.defaultProps is deprecated. Example usage:
 *
 * ```tsx
 * type Props = {
 *     requiredPropA: string;
 *     optionalPropB?: string;
 *     optionalPropC?: string | null;
 * };
 *
 * const MyComponent = withDefaultProps<Props>(
 *     {
 *         optionalPropB: "foo",
 *         optionalPropC: null,
 *     },
 *     (props) => (
 *         <p>
 *             {props.requiredPropA}
 *             {props.optionalPropB}
 *             {props.optionalPropC}
 *         </p>
 *     ),
 * );
 *
 *
 * render((
 *     <>
 *         <MyComponent requiredPropA="foo" />
 *         <MyComponent requiredPropA="foo" optionalPropB="bar" />
 *         <MyComponent requiredPropA="foo" optionalPropC="baz" />
 *     </>
 * ))
 * ```
 *
 */
export const withDefaultProps = <Props>(
    defaults: InvertOptional<Props>,
    Component: FunctionComponent<InvertOptional<Props> & Props>,
): FunctionComponent<Props> => {
    const Wrapped: FunctionComponent<Props> = (props) => {
        return Component({ ...defaults, ...props });
    };
    Wrapped.displayName = `WithDefaultProps(${getDisplayName(Component)})`;
    return Wrapped;
};

type PropsLate<
    PropsAll extends object,
    PropsEarly extends Partial<PropsAll>,
> = {
    [K in keyof PropsAll as PropsEarly[K] extends PropsAll[K]
        ? never
        : K]: PropsAll[K];
};

/**
 * Component decorator for partially applying a React component. Example usage:
 *
 * ```tsx
 * type Props = {
 *     propA: string;
 *     propB: string;
 * };
 *
 * const ComponentA = (props: Props) => (
 *     <p>
 *         {props.propA}
 *         {props.propB}
 *     </p>
 * );
 * const ComponentB = pApplyProps(ComponentA, {
 *     propsB: "baz",
 * })
 *
 * render((
 *     <>
 *         <ComponentA propA="foo" propB="bar" />
 *         <ComponentB propA="foo" />
 *     </>
 * ))
 * ```
 *
 */
export const pApplyProps = <
    PropsAll extends object,
    PropsEarly extends Partial<PropsAll>,
>(
    Component: FunctionComponent<PropsAll>,
    propsEarly: PropsEarly,
): FunctionComponent<PropsLate<PropsAll, PropsEarly>> => {
    const Wrapped = (propsLate: PropsLate<PropsAll, PropsEarly>) => {
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        const props = { ...propsEarly, ...propsLate } as any as PropsAll;
        return Component(props);
    };
    Wrapped.displayName = `WithPApplyProps(${getDisplayName(Component)})`;
    return Wrapped;
};
